// Desktop / Web Push helpers.
//
// Registers the push service worker (/sw.js), requests OS notification
// permission, and manages the per-user push subscription against the API
// (/api/auth/push/*). All of it degrades gracefully: unsupported browsers,
// denied permission, or an API without VAPID keys configured just mean "no
// desktop notifications", never a crash.

import dynamicAPI from '../api/dynamicAPI';

export const SW_URL = '/sw.js';

export const isPushSupported = () =>
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window;

export const getPermission = () =>
    (typeof Notification !== 'undefined' ? Notification.permission : 'default');

// VAPID keys are URL-safe base64; the browser wants a Uint8Array.
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) { output[i] = raw.charCodeAt(i); }
    return output;
}

export async function registerPushServiceWorker() {
    if (!isPushSupported()) { return null; }
    try {
        return await navigator.serviceWorker.register(SW_URL);
    } catch (e) {
        console.error('Push SW registration failed:', e);
        return null;
    }
}

// Is there an active push subscription for this browser right now?
export async function getSubscriptionState() {
    if (!isPushSupported()) { return { supported: false, subscribed: false, permission: 'default' }; }
    const permission = getPermission();
    try {
        const reg = await navigator.serviceWorker.getRegistration(SW_URL);
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        return { supported: true, subscribed: Boolean(sub), permission };
    } catch {
        return { supported: true, subscribed: false, permission };
    }
}

// Full opt-in flow: permission → SW → subscribe → persist on the server.
// Returns { ok, reason } so the UI can explain a failure.
export async function enableDesktopNotifications() {
    if (!isPushSupported()) { return { ok: false, reason: 'unsupported' }; }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') { return { ok: false, reason: 'denied' }; }

    const reg = (await navigator.serviceWorker.getRegistration(SW_URL)) || (await registerPushServiceWorker());
    if (!reg) { return { ok: false, reason: 'sw-failed' }; }
    await navigator.serviceWorker.ready;

    const res = await dynamicAPI('get', '/api/auth/push/public-key');
    if (!res?.enabled || !res?.publicKey) { return { ok: false, reason: 'server-disabled' }; }

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(res.publicKey),
        });
    }

    const json = sub.toJSON();
    await dynamicAPI('post', '/api/auth/push/subscribe', {
        endpoint: sub.endpoint,
        keys: json.keys,
    });
    return { ok: true };
}

export async function disableDesktopNotifications() {
    if (!isPushSupported()) { return { ok: true }; }
    try {
        const reg = await navigator.serviceWorker.getRegistration(SW_URL);
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (sub) {
            const endpoint = sub.endpoint;
            await sub.unsubscribe();
            await dynamicAPI('post', '/api/auth/push/unsubscribe', { endpoint });
        }
        return { ok: true };
    } catch (e) {
        console.error('Disable push failed:', e);
        return { ok: false };
    }
}

// Foreground OS notification (tab open), used by the live socket listener when
// permission is already granted. The service worker handles the background case.
export function showForegroundNotification({ title, body, url }) {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') { return; }
    try {
        const n = new Notification(title || 'SpaceTime', {
            body: body || '',
            icon: '/img/android-chrome-192x192.png',
        });
        n.onclick = () => {
            window.focus();
            if (url) { window.location.assign(url); }
            n.close();
        };
    } catch (e) {
        /* some browsers only allow SW-shown notifications — ignore */
    }
}
